import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CONFIRM_PHONE, SEND_PHONE_CONFIRMATION } from 'dealer-reducers'
import { RootReducer } from 'src/redux/rootReducer'
import EstigroupInput from '../atoms/EstigroupInput'
import NewButton from '../atoms/NewButton'

const PhoneConfirmationModal = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const authorized = useSelector((state: RootReducer) => state.authReducer.authorizedState === 'authorized')
  const phoneConfirmed = useSelector((state: RootReducer) => state.userDataReducer.phone_confirmed)
  const fetchedAt = useSelector((state: RootReducer) => state.userDataReducer.fetchedAt)
  const savedPhone = useSelector((state: RootReducer) => state.userDataReducer.phone ?? '')
  const sending = useSelector((state: RootReducer) => state.userDataReducer.phoneConfirmationSending)
  const sendError = useSelector((state: RootReducer) => state.userDataReducer.phoneConfirmationError)
  const confirming = useSelector((state: RootReducer) => state.userDataReducer.phoneConfirmationConfirming)
  const confirmError = useSelector((state: RootReducer) => state.userDataReducer.phoneConfirmationConfirmError)
  const [phone, setPhone] = useState(savedPhone)
  const [phoneError, setPhoneError] = useState('')
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [sent, setSent] = useState(false)
  const wasSending = useRef(false)

  useEffect(() => {
    if (savedPhone.length > 0) {
      setPhone(savedPhone)
    }
  }, [savedPhone])

  useEffect(() => {
    if (wasSending.current && !sending && sendError === null) {
      setSent(true)
    }
    wasSending.current = sending
  }, [sending, sendError])

  const errorMessage = (): string => {
    if (sendError === 'too_soon') return t('phone_confirmation_modal.too_soon')
    if (sendError === 'limit_reached') return t('phone_confirmation_modal.limit_reached')
    if (sendError !== null) return t('phone_confirmation_modal.error')
    return ''
  }

  const confirmErrorMessage = (): string => {
    if (codeError.length > 0) return codeError
    if (confirmError === 'invalid_code') return t('phone_confirmation_modal.invalid_code')
    if (confirmError === 'code_required') return t('phone_confirmation_modal.code_required')
    if (confirmError !== null) return t('phone_confirmation_modal.confirm_error')
    return ''
  }

  if (!authorized || fetchedAt === 0 || phoneConfirmed) {
    return <></>
  }

  const handlePhoneChange = (value: string): void => {
    setPhone(value)
    setPhoneError('')
    setSent(false)
  }

  const handleSend = (): void => {
    if (phone.trim().length === 0) {
      setPhoneError(t('phone_confirmation_modal.phone_required'))
      return
    }

    setSent(false)
    dispatch(SEND_PHONE_CONFIRMATION({ phone: phone.trim() }))
  }

  const handleConfirm = (): void => {
    if (code.trim().length === 0) {
      setCodeError(t('phone_confirmation_modal.code_required'))
      return
    }

    setCodeError('')
    dispatch(CONFIRM_PHONE({ code: code.trim() }))
  }

  return (
    <div className="fixed inset-0 z-[22223] flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md mx-4 rounded-lg bg-backgrounds-bg-base px-8 py-10">
        <div className="flex flex-col gap-2">
          <div className="text-web-header text-2xl font-bold font-Montserrat leading-8">
            {t('phone_confirmation_modal.title')}
          </div>
          <div className="text-web-text-body text-sm font-normal font-['Open Sans'] leading-snug">
            {t('phone_confirmation_modal.description')}
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-4">
          <EstigroupInput
            label={t('phone_confirmation_modal.phone')}
            placeholder={t('phone_confirmation_modal.phone_placeholder')}
            value={phone}
            onChange={handlePhoneChange}
            type="tel"
            error={phoneError}
          />
          <NewButton buttonVariant="xlarge" buttonType="blue" onClick={handleSend}>
            {sending ? t('phone_confirmation_modal.sending') : t('phone_confirmation_modal.send_sms')}
          </NewButton>
          {sent && !sending && sendError === null && (
            <div className="text-sm text-web-text-body">{t('phone_confirmation_modal.sent')}</div>
          )}
          {errorMessage().length > 0 && <div className="text-sm text-red-500">{errorMessage()}</div>}
          {sent && (
            <>
              <EstigroupInput
                label={t('phone_confirmation_modal.code')}
                placeholder={t('phone_confirmation_modal.code_placeholder')}
                value={code}
                onChange={(value) => {
                  setCode(value)
                  setCodeError('')
                }}
                type="text"
                error={confirmErrorMessage()}
              />
              <NewButton buttonVariant="xlarge" buttonType="blue" onClick={handleConfirm}>
                {confirming ? t('phone_confirmation_modal.confirming') : t('phone_confirmation_modal.confirm')}
              </NewButton>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PhoneConfirmationModal
